import { useState, useEffect, useCallback } from "react";
import { FaQuestionCircle, FaCheckCircle, FaTimesCircle, FaTrophy, FaHistory, FaCopy } from "react-icons/fa";
import { useAuth } from "../../../Authentication/AuthContext";
import { useToastHelpers } from "../../../Toasts/ToastUtils";
import { submitMcqAnswer, fetchTopicSubmissions } from "../../../../apis/SubmissionApis";
import AuthPromptModal from "../../../Authentication/AuthPromptModal";

const MCQView = ({ topic }) => {
  const mcq = topic?.mcq || null;
  const { user, isAuthenticated } = useAuth();
  const { successToast, errorToast } = useToastHelpers();

  const splitGoBlockFromText = (raw) => {
    const text = String(raw ?? "");
    const start = text.indexOf("go:");
    if (start === -1) return { text: text.trim(), code: "" };
    const end = text.indexOf(":go", start + 3);
    if (end === -1) return { text: text.trim(), code: "" };

    const code = text.slice(start + 3, end).replace(/^\n/, "").trimEnd();
    const before = text.slice(0, start).trimEnd();
    const after = text.slice(end + 3).trimStart();
    const cleaned = [before, after].filter(Boolean).join("\n").trim();
    return { text: cleaned, code };
  };

  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [earnedScore, setEarnedScore] = useState(0);
  const [canSubmit, setCanSubmit] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [activeTab, setActiveTab] = useState("problem");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // Fetch existing submissions on mount
  const loadSubmissions = useCallback(async () => {
    if (!isAuthenticated || !topic?.id) return;

    try {
      const data = await fetchTopicSubmissions(topic.id, user?.token);
      setSubmissions(data.submissions || []);
      setCanSubmit(data.canSubmit);

      // If already submitted, restore state
      if (data.submissions && data.submissions.length > 0) {
        const lastSubmission = data.submissions[0];
        setSubmitted(true);
        setSelected(lastSubmission.selectedAnswer);
        setCorrectAnswer(lastSubmission.correctAnswer ?? mcq?.answer);
        setEarnedScore(lastSubmission.score || 0);
      }
    } catch (err) {
      console.error("Failed to load submissions:", err);
    }
  }, [isAuthenticated, topic?.id, user?.token, mcq?.answer]);

  // Copy code to clipboard
  const handleCopyCode = () => {
    const embedded = splitGoBlockFromText(mcq?.question).code;
    const code = embedded || mcq?.code || "";
    if (!code) return;

    navigator.clipboard.writeText(code).then(() => {
      setCodeCopied(true);
      successToast("Code copied to clipboard!");
      setTimeout(() => setCodeCopied(false), 2000);
    }).catch(() => {
      errorToast("Failed to copy code");
    });
  };

  const { text: questionText, code: embeddedCode } = splitGoBlockFromText(mcq?.question);
  const effectiveCode = embeddedCode || mcq?.code || "";

  // Load submissions and topic data
  useEffect(() => {
    if (topic) {
      const timer = setTimeout(() => {
        setIsLoading(false);
        loadSubmissions();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [topic, loadSubmissions]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Loading MCQ...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!mcq) {
    return (
      <div className="ca-card p-8 text-center">
        <p className="text-gray-500 text-lg">No MCQ available for this topic.</p>
      </div>
    );
  }

  const submit = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (selected === null || !canSubmit) return;

    setIsSubmitting(true);
    try {
      const result = await submitMcqAnswer(
        { topicId: topic.id, selectedAnswer: selected },
        user?.token
      );

      setSubmitted(true);
      setCorrectAnswer(result.correctAnswer);
      setEarnedScore(result.score || 0);
      setCanSubmit(false);

      // Reload submissions to update the list
      await loadSubmissions();

      if (result.isCorrect) {
        successToast("Correct answer! 🎉");
      } else {
        errorToast("Incorrect answer. Try again next time!");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to submit answer";
      errorToast(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Correct option: use API response if available, otherwise fallback to mcq.answer
  const actualCorrectAnswer = correctAnswer ?? mcq.answer;
  const isCorrect = selected === actualCorrectAnswer;

  const totalPoints = Number.isFinite(topic?.points) ? topic.points : 0;
  const earnedPoints = earnedScore;

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gradient-to-br from-blue-50 via-white to-purple-50 md:h-screen">
      {/* ===== LEFT PANEL (40%) ===== */}
      <div className="w-full md:w-2/5 bg-white border-b md:border-b-0 md:border-r border-gray-200 overflow-y-auto shadow-sm">
        {/* Problem Header */}
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <FaQuestionCircle className="text-white text-xl" />
            </div>
            <h2 className="text-xl font-bold text-white">
              MCQ Challenge
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-white/20 text-white rounded-full text-sm font-medium backdrop-blur-sm">
              {totalPoints} Points
            </span>
            {submitted && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${isCorrect ? 'bg-green-400 text-green-900' : 'bg-red-400 text-red-900'}`}>
                {isCorrect ? '✓ Solved' : '✗ Attempted'}
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-gray-200 sticky top-0 bg-white z-10">
          <button
            onClick={() => setActiveTab("problem")}
            className={`flex-1 py-4 text-sm font-semibold border-b-2 transition flex items-center justify-center gap-2 ${
              activeTab === "problem"
                ? "text-blue-600 border-blue-600 bg-blue-50/50"
                : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <FaQuestionCircle /> Problem
          </button>
          <button
            onClick={() => setActiveTab("submissions")}
            className={`flex-1 py-4 text-sm font-semibold border-b-2 transition flex items-center justify-center gap-2 ${
              activeTab === "submissions"
                ? "text-blue-600 border-blue-600 bg-blue-50/50"
                : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <FaHistory /> Submissions ({submissions.length})
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === "problem" && (
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">
                  Problem Statement
                </h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {questionText || mcq?.question}
                </p>
              </div>

              {/* Code Block - Same styling as TheoryView */}
              {effectiveCode && (
                <div className="my-6">
                  <div className="rounded-xl overflow-hidden shadow-lg border border-gray-800">
                    <div className="px-4 py-3 bg-gray-800 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500" />
                          <div className="w-3 h-3 rounded-full bg-yellow-500" />
                          <div className="w-3 h-3 rounded-full bg-green-500" />
                        </div>
                        <span className="ml-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Go</span>
                      </div>
                      <button
                        onClick={handleCopyCode}
                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-colors text-xs font-medium"
                        title="Copy code"
                      >
                        <FaCopy size={12} />
                        {codeCopied ? "Copied!" : "Copy"}
                      </button>
                    </div>
                    <pre className="p-5 bg-gray-900 overflow-x-auto text-sm leading-relaxed">
                      <code className="text-green-400 font-mono">{effectiveCode}</code>
                    </pre>
                  </div>
                </div>
              )}

              {/* Show solution after submission */}
              {submitted && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Solution description
                  </h4>
                  <p className="text-gray-700 text-sm leading-relaxed bg-gray-50 p-4 rounded-lg">
                    {mcq.explanation || "No explanation provided for this question."}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "submissions" && (
            <div className="space-y-4">
              {submissions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No submissions yet
                </div>
              ) : (
                submissions.map((sub, idx) => (
                  <div
                    key={sub.id || idx}
                    className={`p-4 rounded-lg border ${
                      sub.isCorrect
                        ? "bg-green-50 border-green-200"
                        : "bg-red-50 border-red-200"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-900">
                        Attempt #{sub.attemptNumber || idx + 1}
                      </span>
                      <span
                        className={`text-sm font-bold ${
                          sub.isCorrect ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {sub.isCorrect ? "✓ Correct" : "✗ Incorrect"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>
                        Score: <span className="font-semibold">{sub.score}/{sub.maxScore}</span>
                      </p>
                      <p>
                        Selected: <span className="font-semibold">{mcq.options?.[sub.selectedAnswer] || `Option ${sub.selectedAnswer + 1}`}</span>
                      </p>
                      <p>
                        Submitted: {new Date(sub.submittedAt * 1000).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* ===== RIGHT PANEL (60%) ===== */}
      <div className="w-full md:w-3/5 flex flex-col bg-white md:h-screen">
        {/* Header */}
        <div className="border-b border-gray-200 p-6 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FaCheckCircle className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Select Your Answer</p>
                <p className="text-xs text-gray-500">Choose the correct option below</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl">
              <FaTrophy className="text-yellow-500" />
              <span className="font-bold text-gray-900">{earnedPoints}/{totalPoints}</span>
            </div>
          </div>
        </div>

        {/* Options Section - Fixed Height */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col">
          {mcq.options && mcq.options.length > 0 ? (
            <>
              {/* Options with fixed spacing */}
              <div className="space-y-4 mb-6">
                {mcq.options.map((op, index) => {
                  const isSelected = selected === index;
                  const isCorrectOption = submitted && index === actualCorrectAnswer;
                  const isWrongSelected = submitted && isSelected && !isCorrect;
                  
                  let optionStyle = "border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 hover:shadow-sm";
                  if (isSelected && !submitted) {
                    optionStyle = "border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200";
                  } else if (isCorrectOption) {
                    optionStyle = "border-green-500 bg-green-50 shadow-md";
                  } else if (isWrongSelected) {
                    optionStyle = "border-red-500 bg-red-50 shadow-md";
                  }

                  return (
                    <label
                      key={index}
                      className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${optionStyle}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-4 flex-shrink-0 ${
                        isSelected && !submitted ? 'bg-blue-600 text-white' :
                        isCorrectOption ? 'bg-green-600 text-white' :
                        isWrongSelected ? 'bg-red-600 text-white' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </div>
                      <input
                        type="radio"
                        name="mcq"
                        disabled={submitted}
                        checked={selected === index}
                        onChange={() => setSelected(index)}
                        className="hidden"
                      />
                      <span className="text-gray-800 flex-1 font-medium">{op}</span>

                      {/* Show icons after submission */}
                      {isCorrectOption && (
                        <FaCheckCircle className="text-green-600 text-xl ml-2 flex-shrink-0" />
                      )}
                      {isWrongSelected && (
                        <FaTimesCircle className="text-red-600 text-xl ml-2 flex-shrink-0" />
                      )}
                    </label>
                  );
                })}
              </div>

              {/* Result Section */}
              {submitted && selected !== null && (
                <div
                  className={`p-5 rounded-xl border-2 mt-auto ${
                    isCorrect
                      ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-300"
                      : "bg-gradient-to-r from-red-50 to-orange-50 border-red-300"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    {isCorrect ? (
                      <FaCheckCircle className="text-green-600 text-2xl" />
                    ) : (
                      <FaTimesCircle className="text-red-600 text-2xl" />
                    )}
                    <p className={`font-bold text-lg ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                      {isCorrect ? "Correct Answer!" : "Incorrect Answer"}
                    </p>
                  </div>
                  <p className={`text-sm ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                    {isCorrect
                      ? "Great job! You've mastered this concept."
                      : `The correct answer was: ${mcq.options[actualCorrectAnswer]}`}
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500">No options available for this question.</p>
          )}
        </div>

        {/* Bottom Action Buttons */}
        <div className="border-t border-gray-200 p-5 bg-gradient-to-r from-gray-50 to-white">
          {!submitted && canSubmit && (
            <button
              onClick={submit}
              disabled={selected === null || isSubmitting}
              className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
            >
              {isSubmitting ? "Submitting..." : "Submit Answer"}
            </button>
          )}

          {submitted && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 text-blue-800 p-4 rounded-xl text-center">
              <p className="font-semibold">MCQ Completed</p>
              <p className="text-sm text-blue-600 mt-1">Only 1 attempt allowed per question</p>
            </div>
          )}

          {!submitted && !canSubmit && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-300 text-yellow-800 p-4 rounded-xl text-center">
              <p className="font-semibold">No Attempts Remaining</p>
              <p className="text-sm text-yellow-600 mt-1">You've used all available attempts</p>
            </div>
          )}
        </div>
      </div>

      {/* Auth Prompt Modal */}
      <AuthPromptModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        message="Please log in to submit your answer."
      />
    </div>
  );
};

export default MCQView;
